<script setup lang="ts">
/**
 * Call buy-template prices editor.
 * Wire format: `{ [country_iso]: { [operator_id]: price } }`.
 */
import { ref, watch } from 'vue';
import { DxButton } from 'devextreme-vue/button';
import { DxNumberBox } from 'devextreme-vue/number-box';
import { LookupBox } from '@botto/core';
import { getCountriesLookup, getOperatorsLookup } from '../../lookups';
import type { BuyTemplateCallEntry } from '../../api/normalizers';

const props = defineProps<{
  modelValue: BuyTemplateCallEntry[];
}>();
const emit = defineEmits<{
  (e: 'update:modelValue', v: BuyTemplateCallEntry[]): void;
}>();

const entries = ref<BuyTemplateCallEntry[]>([]);
const countriesLookup = getCountriesLookup();
const operatorsLookup = getOperatorsLookup();

watch(
  () => props.modelValue,
  (v) => {
    entries.value = v.map((e) => ({ ...e }));
  },
  { immediate: true, deep: true },
);

function pushUpdate(): void {
  emit('update:modelValue', entries.value);
}

function addEntry(): void {
  entries.value.push({ country_iso: '', operator_id: 0, price: 0 });
  pushUpdate();
}

function removeEntry(index: number): void {
  entries.value.splice(index, 1);
  pushUpdate();
}
</script>

<template>
  <div class="bt-calls-editor">
    <div class="bt-toolbar">
      <DxButton icon="add" text="Добавить строку" type="default" styling-mode="contained" @click="addEntry" />
    </div>

    <div v-if="entries.length === 0" class="bt-empty">Цены звонков не заданы.</div>

    <div v-for="(entry, idx) in entries" :key="idx" class="bt-entry">
      <div class="bt-fields">
        <label>
          <span>Страна</span>
          <LookupBox
            v-model="entry.country_iso"
            :lookup="countriesLookup"
            placeholder="Страна"
            @update:model-value="pushUpdate"
          />
        </label>
        <label>
          <span>Оператор</span>
          <LookupBox
            v-model="entry.operator_id"
            :lookup="operatorsLookup"
            placeholder="Оператор"
            @update:model-value="pushUpdate"
          />
        </label>
        <label>
          <span>Цена</span>
          <DxNumberBox
            v-model:value="entry.price"
            :format="{ type: 'fixedPoint', precision: 4 }"
            @value-changed="pushUpdate"
          />
        </label>
      </div>
      <DxButton icon="trash" hint="Удалить строку" styling-mode="text" @click="removeEntry(idx)" />
    </div>
  </div>
</template>

<style scoped>
.bt-calls-editor { display: flex; flex-direction: column; gap: 12px; padding-top: 8px; }
.bt-toolbar { display: flex; justify-content: flex-end; }
.bt-empty { color: #9ca3af; padding: 24px; text-align: center; border: 1px dashed #e5e7eb; border-radius: 4px; }
.bt-entry { display: flex; align-items: flex-end; gap: 12px; border: 1px solid #e5e7eb; border-radius: 6px; padding: 12px; }
.bt-fields { display: flex; gap: 12px; flex-wrap: wrap; flex: 1; }
.bt-fields label { display: flex; flex-direction: column; gap: 4px; font-size: 12px; color: #555; min-width: 180px; }
</style>
